import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoggerService } from '../services/logger.service';
import { Estimation } from '../models/estimacion.model';
import { AuthService } from '../auth/auth.service';
import { EstimationVersion } from '../models/estimation-version.model';

@Injectable({
  providedIn: 'root'
})
export class EstimacionService {

  constructor(private http: HttpClient, private logger: LoggerService, private authService: AuthService) {}

  getUltimasEstimaciones(userId: number): Observable<any[]> {
    return this.http.get<any[]>(`${environment.api_service_estimation}/user/${userId}/last`);
  }

  getEstimationById(id: string): Observable<any> {
    return this.http.get<any>(`${environment.api_service_estimation}/${id}`);
  }

  getEstimationVersions(id: string): Observable<EstimationVersion[]> {
    return this.http.get<EstimationVersion[]>(`${environment.api_service_estimation}/${id}/versions`);
  }

  saveEstimation(estimacion: Estimation): Observable<any> {
    this.logger.log("EstimacionService.saveEstimation");
    const preparedEstimation = this.prepareEstimationForBackend(estimacion);
    const headers = { 'Content-Type': 'application/json' };

    if (estimacion.id) {
      return this.http.put(`${environment.api_service_estimation}/${preparedEstimation.id}`, preparedEstimation, { headers });
    } else {
      const preparedProject = this.prepareProjectForBackend(estimacion);
      preparedProject.estimation = preparedEstimation;
      this.logger.log("EstimacionService.preparedProject: ", preparedProject);
      const newProject = this.http.post<any>(environment.api_service_createProjectRAG, preparedProject, { headers });

      return newProject.pipe(
        map(response => response.estimation)
      );
    }
  }

  deleteEstimation(id: string): Observable<any> {
    return this.http.delete(`${environment.api_service_estimation}/${id}`);
  }

  /**
   * Stream RAG-PT con parser SSE robusto a fragmentación TCP.
   *
   * Implementación: mantiene un buffer acumulativo entre chunks. Solo procesa
   * eventos SSE COMPLETOS (delimitados por '\n\n'). Los datos parciales quedan
   * en el buffer hasta el siguiente chunk. Esto es robusto frente a:
   *   - Buffering de Nginx/Cloudflare/load balancers que agrupa eventos.
   *   - Fragmentación TCP que parte un evento a mitad.
   *   - Caracteres UTF-8 multi-byte cortados en frontera de chunk
   *     (TextDecoder con {stream: true} ya lo gestiona).
   */
  async streamRAGPT(estimacion: Estimation, onChunk: (text: string) => void): Promise<any> {
    this.logger.log("EstimacionService.streamRAGPT");

    const preparedInput = this.prepareCallGPT(estimacion);

    // Obtenemos el token para enviarlo manualmente ya que HttpClient (interceptors) no intercepta fetch
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(environment.api_service_ragpt, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(preparedInput)
      });
    } catch (networkErr: any) {
      this.logger.error('Error de red en streamRAGPT', networkErr);
      throw new Error('No se pudo conectar con el servidor de IA. Verifica tu conexión.');
    }

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      this.logger.error('Respuesta no OK en streamRAGPT', { status: response.status, body: text });
      if (response.status >= 500) throw new Error('Servicio de IA no disponible. Inténtalo más tarde.');
      let errorMessage = 'No se pudo generar la estimación. Revisa los datos e inténtalo de nuevo.';
      try {
        const body = JSON.parse(text);
        if (body.message) errorMessage = body.message;
      } catch (e) {}
      throw new Error(errorMessage);
    }

    if (!response.body) throw new Error("No ReadableStream available");

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');

    /** Texto JSON acumulado del modelo (solo los `content` chunks). */
    let fullText = '';
    /** Buffer SSE acumulativo entre lecturas — parser robusto a fragmentación. */
    let sseBuffer = '';
    /** Metadatos de tokens, si llegan. */
    let metadataStorage: any = null;
    /** JSON saneado tras reconciliación backend, si llega. */
    let finalFromBackend: any = null;

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        // Procesar último resto del buffer si hay algo (evento sin \n\n final).
        if (sseBuffer.trim().length > 0) {
          this.processSseEvent(sseBuffer, (content) => {
            fullText += content;
            onChunk(fullText);
          }, (meta) => {
            metadataStorage = meta;
          }, (final) => {
            finalFromBackend = final;
            this.logger.log('[STREAM] Recibido _final del backend (rollup reconciliado)');
          });
        }
        break;
      }

      // Acumular el chunk en el buffer y procesar eventos COMPLETOS.
      sseBuffer += decoder.decode(value, { stream: true });

      // Un evento SSE termina con '\n\n'. Procesamos todos los completos
      // y dejamos el resto (potencialmente parcial) en el buffer.
      let eventEnd = sseBuffer.indexOf('\n\n');
      while (eventEnd !== -1) {
        const eventStr = sseBuffer.substring(0, eventEnd);
        sseBuffer = sseBuffer.substring(eventEnd + 2);

        this.processSseEvent(eventStr, (content) => {
          fullText += content;
          onChunk(fullText);
        }, (meta) => {
          metadataStorage = meta;
        }, (final) => {
          finalFromBackend = final;
          this.logger.log('[STREAM] Recibido _final del backend (rollup reconciliado)');
        });

        eventEnd = sseBuffer.indexOf('\n\n');
      }
    }

    // Decisión final: si el backend envió _final (rollup reconciliado), prevalece.
    // Si no, parseamos el fullText acumulado.
    try {
      const finalObject = finalFromBackend ?? JSON.parse(fullText);
      if (metadataStorage) {
        finalObject._metadata = metadataStorage;
      }
      return finalObject;
    } catch (e: any) {
      // Logging defensivo: información para diagnóstico sin exponer datos sensibles.
      this.logger.error('[STREAM] Error parseando respuesta final del modelo', {
        errorMessage: e?.message,
        fullTextLength: fullText?.length || 0,
        fullTextStart: fullText?.substring(0, 200),
        fullTextEnd: fullText?.substring(Math.max(0, (fullText?.length || 0) - 200)),
        sseBufferLeftover: sseBuffer?.substring(0, 200),
        hasFinalFromBackend: !!finalFromBackend,
        hasMetadata: !!metadataStorage
      });
      throw new Error("El modelo generó un JSON inválido");
    }
  }

  /**
   * Procesa un evento SSE COMPLETO (sin '\n\n' final).
   * Un evento puede tener varias líneas, pero típicamente solo 'data: ...'.
   * Ignora silenciosamente eventos vacíos, comentarios SSE (':'), o el
   * marcador final '[DONE]'.
   */
  private processSseEvent(
    eventStr: string,
    onContent: (content: string) => void,
    onMetadata: (meta: any) => void,
    onFinal: (final: any) => void
  ): void {
    const lines = eventStr.split('\n');
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (trimmed.startsWith(':')) continue;
      if (!trimmed.startsWith('data:')) continue;

      const dataStr = trimmed.substring(5).trim();
      if (dataStr === '[DONE]') return;

      try {
        const parsed = JSON.parse(dataStr);
        if (typeof parsed.content === 'string') {
          onContent(parsed.content);
        }
        if (parsed._metadata) {
          onMetadata(parsed._metadata);
        }
        if (parsed._final) {
          onFinal(parsed._final);
        }
      } catch (e) {
        // Si llegamos aquí con un evento "completo" que no parsea, es un
        // chunk genuinamente corrupto. Loggeamos para diagnóstico futuro.
        this.logger.warn('[STREAM] Evento SSE no parseable (descartado)', {
          eventPreview: dataStr.substring(0, 100)
        });
      }
    }
  }

  private prepareEstimationForBackend(estimacion: Estimation): any {
    const currentUser = this.authService.getCurrentUser();
    return {
      ...estimacion,
      projectid: estimacion.project?.id,
      userid: currentUser?.id,
      project: undefined,
      systems: undefined
    };
  }

  private prepareProjectForBackend(estimacion: Estimation): any {
    const currentUser = this.authService.getCurrentUser();
    // Punto B: type y department se leen desde estimacion.project porque
    // ya no existen como propiedades directas en la estimación.
    return {
      name: estimacion.name,
      creationdate: this.setFechaActual(),
      description: estimacion.name,
      typeid: estimacion.project?.type?.id,
      departmentid: estimacion.project?.department?.id,
      tenantid: currentUser?.tenant?.id,
      userid: currentUser?.id,
      scope: estimacion.prompt,
      systemIds: (estimacion.systems || []).map(s => s.id)
    };
  }

  setFechaActual(): any {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private prepareCallGPT(estimacion: Estimation): any {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      // Priorizar sistemas in-flight (wizard); caer al proyecto existente.
      const systems = (estimacion.systems && estimacion.systems.length > 0)
        ? estimacion.systems
        : (estimacion.project?.systems || []);

      return {
        input: estimacion.prompt,
        societyId: estimacion.project?.department?.society?.id,
        departmentId: estimacion.project?.department?.id,
        estimationType: estimacion.project?.type?.id,
        systemIds: systems.map(s => s.id),
        // 1b.2: enviamos también los nombres para que el backend pueda
        // calcular slugs en deriveSystemSlugsFromFilters() y aplicar el
        // matching cross-cliente del canal Tipo+Sistema del RAG.
        // Sin esto, el canal cae a filtrado por id puro y no encuentra
        // proyectos del mismo sistema en otros clientes.
        systemNames: systems.map(s => s.name),
        tenantid: currentUser?.tenant?.id,
        userid: currentUser?.id,
        projectId: estimacion.project?.id,
        estimationId: estimacion.id
      };
    }
    return null;
  }
}